import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import { Fragment } from "react";

import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import {
  LandingImage,
  LandingSection,
  SectionHeading,
} from "../LandingPrimitives";
import type { Cards } from "./cardsShared";
import { BENEFITS_SECTION_FOOT } from "./landingBands";

/**
 * `.benefits_list.is-affiliate`: the "How it works" strip the affiliate and
 * referral pages draw. Four numbered tiles in a row, each a picture over its
 * title and line, joined by dashed rules at the height of the pictures; below
 * the card step the rules go and the tiles stack as bordered cards, with the
 * numeral moving in from the tile's corner to the card's.
 */

/**
 * `.benefits_wrap`'s top margin and `.container_new.padding-bottom`'s close,
 * both as padding: the published container's clearfix keeps that margin
 * inside the section, where here it would collapse through and move the
 * section instead.
 */
const BREAK = {
  pt: "100px",
  pb: "48px",
  [NOT_WIDE]: { pt: "64px", pb: "64px" },
  [TINY]: { pt: "48px", pb: "48px" },
} satisfies SxProps<Theme>;

/** `.benefits_wrap`: the heading over the list. */
const WRAP = {
  display: "flex",
  flexDirection: "column",
  gap: "40px",
  [MID]: { gap: "32px" },
  [TINY]: { gap: "24px" },
} satisfies SxProps<Theme>;

const LIST = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  [MID]: { flexDirection: "column", alignItems: "center", gap: "32px" },
  [TINY]: { gap: "16px" },
} satisfies SxProps<Theme>;

/** `.ben-aff_item`: bare in the row, a bordered card once stacked. */
const TILE = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
  width: 1,
  [MID]: {
    p: "32px",
    borderRadius: "16px",
    border: `1px solid ${marketingPalette.wash}`,
  },
  [TINY]: { gap: "16px", p: "24px" },
} satisfies SxProps<Theme>;

/** `.ben-aff-item_img`: a fixed square whatever the tile's width. */
const ART = {
  width: "188px",
  aspectRatio: "1",
} satisfies SxProps<Theme>;

/** `.ben-aff-item_bot` */
const COPY = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  textAlign: "center",
} satisfies SxProps<Theme>;

/** `.ben-aff-item_title` */
const TITLE = {
  color: marketingPalette.ink,
  fontSize: 22.08,
  lineHeight: "26.496px",
  fontWeight: 500,
  [MID]: { fontSize: 18.4, lineHeight: "22.08px" },
} satisfies SxProps<Theme>;

/** `.body_new.is-very_small.grey_txt`: the grey line, quieted a step further, set left as `.body_new` is. */
const LINE = {
  color: marketingPalette.inkSubtle,
  opacity: 0.8,
  fontSize: 14,
  lineHeight: "19.6px",
  textAlign: "left",
} satisfies SxProps<Theme>;

/** `.ben-aff-item_number`: the numeral in the tile's corner, inset once the tile has a border. */
const NUMERAL = {
  position: "absolute",
  top: 0,
  left: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "30.08px",
  height: "30.08px",
  color: marketingPalette.ink,
  fontSize: 14.08,
  lineHeight: "21.12px",
  fontWeight: 600,
  opacity: 0.9,
  [MID]: { top: "16px", left: "16px" },
} satisfies SxProps<Theme>;

/** `.ben-aff_divider-wrap`: the rule between tiles, centred on the pictures' height. */
const RULE_WRAP = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "188px",
  [NOT_WIDE]: { display: "none" },
} satisfies SxProps<Theme>;

/** `.ben-aff_divider` */
const RULE = {
  width: "50.08px",
  border: "0.5px dashed rgba(79, 92, 101, 0.6)",
} satisfies SxProps<Theme>;

export default function CardsSteps({ block }: { block: Cards }) {
  return (
    <LandingSection padding="none" sx={[BREAK, BENEFITS_SECTION_FOOT]}>
      <Box sx={WRAP}>
        <SectionHeading title={block.title} align="center" />
        <Box sx={LIST}>
          {block.items.map((card, index) => (
            <Fragment key={index}>
              {index > 0 ? (
                <Box sx={RULE_WRAP} aria-hidden>
                  <Box sx={RULE} />
                </Box>
              ) : null}
              <Step card={card} />
            </Fragment>
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}

function Step({ card }: { card: Card }) {
  return (
    <Box sx={TILE}>
      {card.icon ? <LandingImage src={card.icon} sx={ART} /> : null}
      <Box sx={COPY}>
        <Box sx={TITLE}>{card.title}</Box>
        {card.body ? <Box sx={LINE}>{card.body}</Box> : null}
      </Box>
      {card.step ? <Box sx={NUMERAL}>{card.step}</Box> : null}
    </Box>
  );
}
